import { intlLocaleFor } from "../i18n/locales";
import { useState, useEffect, useCallback } from "react";
import {
  Loader2, Building2, CreditCard, FileText, Download, Plus, Trash2, Star,
  Ban, Check, FlaskConical, Landmark,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import { api, apiError } from "../lib/api";
import { fmtAmountMinor } from "../lib/usePlans";
import i18n, { countryName } from "../i18n";

// Traduction (Mon espace) : les textes suivent la langue active ; la page parente s'abonne aux changements via useTranslation.
const tr = (k, o) => i18n.t(k, o);

const DOC_STATUS_BADGE = {
  paid: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  pending: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  unpaid: "bg-red-500/15 text-red-600 border-red-500/30",
  overdue: "bg-red-500/15 text-red-600 border-red-500/30",
  blocked: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  cancelled: "bg-slate-500/15 text-slate-500 border-slate-500/30",
  credited: "bg-cyan-500/15 text-cyan-600 border-cyan-500/30",
};
// Codes métier facturation V1 -> message traduit (jamais de message brut provider).
export const billingErrorMessage = (detail) => {
  const code = detail && typeof detail === "object" ? detail.code : null;
  return code && i18n.exists(`billing:v1.errors.${code}`) ? tr(`billing:v1.errors.${code}`) : (apiError(detail) || tr("billing:v1.errors.generic"));
};
const PM_TYPE_LABEL = { card: "Carte bancaire", get sepa_debit() { return tr("billing:panel.prelevementAutomatique"); }, bank_transfer: "Virement bancaire" };

export const BillingPanel = ({ workspaceId, role, plan }) => {
  const canEditCompany = role === "owner" || role === "billing_admin";
  const canManage = role === "owner" || role === "admin" || role === "billing_admin";

  const [company, setCompany] = useState(null);
  const [companyDraft, setCompanyDraft] = useState({});
  const [countries, setCountries] = useState([]);
  const [savingCompany, setSavingCompany] = useState(false);
  const [methods, setMethods] = useState([]);
  const [docs, setDocs] = useState([]);
  const [filters, setFilters] = useState({ type: "all", status: "all", q: "" });
  const [loading, setLoading] = useState(true);
  const [providerStatus, setProviderStatus] = useState(null);
  const [redirecting, setRedirecting] = useState(null);

  const loadDocs = useCallback(async () => {
    const params = {};
    if (filters.type !== "all") params.type = filters.type;
    if (filters.status !== "all") params.status = filters.status;
    if (filters.q) params.q = filters.q;
    const { data } = await api.get(`/workspaces/${workspaceId}/billing/documents`, { params });
    setDocs(data);
  }, [workspaceId, filters]);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const [c, m, ps, countryRes] = await Promise.all([
        api.get(`/workspaces/${workspaceId}/billing/company`),
        api.get(`/workspaces/${workspaceId}/payment-methods`),
        api.get(`/workspaces/${workspaceId}/billing/provider-status`).catch(() => ({ data: null })),
        api.get(`/countries`),
      ]);
      setCompany(c.data); setCompanyDraft(c.data); setMethods(m.data); setProviderStatus(ps.data);
      setCountries(countryRes.data.countries || []);
      await loadDocs();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  // Retour de la session portefeuille Mobupay : l'URL n'est JAMAIS une preuve -> resynchronisation serveur.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!workspaceId || params.get("card") !== "return") return;
    params.delete("card"); params.delete("status"); params.delete("paymentMethodId");
    window.history.replaceState({}, "", `${window.location.pathname}${params.toString() ? `?${params}` : ""}`);
    api.post(`/workspaces/${workspaceId}/payment-methods/sync`)
      .then((r) => { setMethods(r.data); toast.success(tr("billing:v1.cardSynced")); })
      .catch((e) => toast.error(billingErrorMessage(e.response?.data?.detail)));
  }, [workspaceId]);

  const goProvider = async (key, url, body) => {
    setRedirecting(key);
    try {
      const { data } = await api.post(url, body);
      window.location.assign(data.redirect_url);
    } catch (e) { toast.error(billingErrorMessage(e.response?.data?.detail)); setRedirecting(null); }
  };
  const addBankTransfer = async () => {
    try { await api.post(`/workspaces/${workspaceId}/payment-methods`, { type: "bank_transfer" }); toast.success(tr("billing:panel.moyenDePaiementAjoute")); load(); }
    catch (e) { toast.error(billingErrorMessage(e.response?.data?.detail)); }
  };
  useEffect(() => { if (!loading) loadDocs(); }, [filters]);

  const saveCompany = async () => {
    setSavingCompany(true);
    try {
      const a = companyDraft.billing_address || {};
      const addressComplete = !!(a.line1 && a.city && a.postal_code && a.country_code);
      const payload = {
        company_name: companyDraft.company_name, trade_name: companyDraft.trade_name,
        registration_number: companyDraft.registration_number, vat_number: companyDraft.vat_number,
        company_phone: companyDraft.company_phone, billing_email: companyDraft.billing_email || null,
        billing_contact_name: companyDraft.billing_contact_name, invoice_to_owner: !!companyDraft.invoice_to_owner,
        billing_address: addressComplete ? {
          line1: a.line1.trim(), line2: (a.line2 || "").trim() || null,
          city: a.city.trim(), postal_code: a.postal_code.trim(), country_code: a.country_code,
        } : null,
      };
      const { data } = await api.put(`/workspaces/${workspaceId}/billing/company`, payload);
      setCompany(data); setCompanyDraft(data); toast.success(tr("billing:panel.informationsEnregistrees"));
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "REGION_CHANGE_REQUIRES_PROCEDURE") toast.error(tr("billing:panel.changementDePaysUne"));
      else toast.error(apiError(d));
    } finally { setSavingCompany(false); }
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(`/workspaces/${workspaceId}/billing/documents/${doc.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${tr(`billing:docType.${doc.doc_type}`, { defaultValue: doc.doc_type_label })}-${doc.number}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(tr("billing:panel.telechargementImpossible")); }
  };

  if (loading || !company) {
    return <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="billing-panel">
      {/* Informations entreprise (§32) */}
      <div className="glass rounded-2xl p-6" data-testid="company-info-section">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="w-5 h-5 text-emerald-600" />
          <h3 className="font-heading text-lg font-semibold">{tr("billing:panel.informationsEntreprise")}</h3>
          {!canEditCompany && <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-200">{tr("billing:panel.lectureSeule")}</Badge>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ["company_name", "Raison sociale"], ["trade_name", "Nom commercial"],
            ["registration_number", tr("billing:panel.numeroDImmatriculation")], ["vat_number", tr("billing:panel.numeroDeTva")],
            ["company_phone", tr("billing:panel.telephoneEntreprise")], ["billing_email", tr("billing:panel.eMailDeFacturation")],
            ["billing_contact_name", tr("billing:panel.contactFacturation")],
          ].map(([k, lbl]) => (
            <div key={k}>
              <Label className="text-slate-600 mb-1.5 block">{lbl}</Label>
              <Input data-testid={`company-${k}`} value={companyDraft[k] || ""} disabled={!canEditCompany}
                onChange={(e) => setCompanyDraft((d) => ({ ...d, [k]: e.target.value }))}
                className="bg-slate-50 border-slate-200 disabled:opacity-70" />
            </div>
          ))}
          <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
            <div className="font-medium text-sm text-slate-800">Adresse de facturation</div>
            <div>
              <Label className="text-slate-600 mb-1.5 block">Adresse</Label>
              <Input value={companyDraft.billing_address?.line1 || ""} disabled={!canEditCompany}
                onChange={(e) => setCompanyDraft((d) => ({ ...d, billing_address: { ...(d.billing_address || {}), line1: e.target.value } }))}
                className="bg-white border-slate-200 disabled:opacity-70" />
            </div>
            <div>
              <Label className="text-slate-600 mb-1.5 block">Complément</Label>
              <Input value={companyDraft.billing_address?.line2 || ""} disabled={!canEditCompany}
                onChange={(e) => setCompanyDraft((d) => ({ ...d, billing_address: { ...(d.billing_address || {}), line2: e.target.value } }))}
                className="bg-white border-slate-200 disabled:opacity-70" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-slate-600 mb-1.5 block">Code postal</Label>
                <Input value={companyDraft.billing_address?.postal_code || ""} disabled={!canEditCompany}
                  onChange={(e) => setCompanyDraft((d) => ({ ...d, billing_address: { ...(d.billing_address || {}), postal_code: e.target.value } }))}
                  className="bg-white border-slate-200 disabled:opacity-70" />
              </div>
              <div>
                <Label className="text-slate-600 mb-1.5 block">Ville</Label>
                <Input value={companyDraft.billing_address?.city || ""} disabled={!canEditCompany}
                  onChange={(e) => setCompanyDraft((d) => ({ ...d, billing_address: { ...(d.billing_address || {}), city: e.target.value } }))}
                  className="bg-white border-slate-200 disabled:opacity-70" />
              </div>
              <div>
                <Label className="text-slate-600 mb-1.5 block">{tr("billing:panel.pays")}</Label>
                <select value={companyDraft.billing_address?.country_code || ""} disabled={!canEditCompany}
                  onChange={(e) => setCompanyDraft((d) => ({ ...d, billing_address: { ...(d.billing_address || {}), country_code: e.target.value } }))}
                  className="w-full rounded-md bg-white border border-slate-200 text-slate-900 px-3 py-2 text-sm disabled:opacity-70">
                  <option value="">Choisir un pays…</option>
                  {countries.slice().sort((a, b) => countryName(a.country_code, i18n.language).localeCompare(countryName(b.country_code, i18n.language), i18n.language)).map((c) => (
                    <option key={c.country_code} value={c.country_code}>{countryName(c.country_code, i18n.language)} ({c.country_code})</option>
                  ))}
                </select>
                <p className="text-xs text-slate-400 mt-1">{tr("billing:panel.leChangementDePays")}</p>
              </div>
            </div>
          </div>
        </div>
        {canEditCompany && (
          <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" data-testid="invoice-to-owner" checked={!!companyDraft.invoice_to_owner}
                onChange={(e) => setCompanyDraft((d) => ({ ...d, invoice_to_owner: e.target.checked }))} />
              {tr("billing:panel.envoyerAussiLesFactures")}
            </label>
            <Button data-testid="save-company-btn" onClick={saveCompany} disabled={savingCompany}
              className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
              {savingCompany ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}{tr("billing:panel.enregistrer")}
            </Button>
          </div>
        )}
        <p className="text-xs text-slate-400 mt-3">
          {tr("billing:panel.lEMailDe")}
        </p>
      </div>

      {/* Moyens de paiement (§36/§37) */}
      <div className="glass rounded-2xl p-6" data-testid="payment-methods-section">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-cyan-600" />
            <h3 className="font-heading text-lg font-semibold">{tr("billing:panel.moyensDePaiement")}</h3>
          </div>
          {canManage && (
            <div className="flex flex-wrap gap-2">
              {plan === "enterprise" && (
                <Button data-testid="add-bank-transfer-btn" onClick={addBankTransfer} variant="outline"
                  className="border-slate-200 bg-white hover:bg-slate-100 text-slate-900">
                  <Landmark className="w-4 h-4 mr-2" /> {tr("billing:v1.addBankTransfer")}
                </Button>
              )}
              <Button data-testid="add-card-btn" disabled={!!redirecting}
                onClick={() => goProvider("card", `/workspaces/${workspaceId}/payment-methods/session`)}
                className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
                {redirecting === "card" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} {tr("billing:v1.addCard")}
              </Button>
            </div>
          )}
        </div>
        {providerStatus?.test_mode && (
          <p data-testid="billing-test-mode" className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs text-amber-800">
            <FlaskConical className="w-3.5 h-3.5" /> {tr("billing:v1.testMode")}
          </p>
        )}
        {methods.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center">{tr("billing:panel.aucunMoyenDePaiement")}</p>
        ) : (
          <div className="space-y-2" data-testid="payment-methods-list">
            {methods.map((pm) => (
              <div key={pm.id} data-testid={`payment-method-${pm.id}`}
                className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                    {tr(`billing:pmType.${pm.type}`, { defaultValue: pm.type_label })}{pm.brand ? ` ${pm.brand}` : ""}{pm.last4 ? ` ••••${pm.last4}` : ""}
                    {pm.is_default && <Badge variant="outline" className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30">{tr("billing:panel.parDefaut")}</Badge>}
                  </div>
                  <div className="text-xs text-slate-500">
                    {tr("billing:panel.statut")} {pm.status === "unverified" ? tr("billing:panel.aVerifierPaiementNon") : tr(`billing:v1.pmStatus.${pm.status}`, { defaultValue: pm.status })}
                  </div>
                </div>
                {canManage && (
                  <div className="flex items-center gap-1 shrink-0">
                    {!pm.is_default && (
                      <Button size="sm" variant="ghost" data-testid={`set-default-pm-${pm.id}`}
                        onClick={async () => { try { await api.post(`/workspaces/${workspaceId}/payment-methods/${pm.id}/default`); toast.success(tr("billing:panel.moyenParDefautMis")); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } }}
                        className="text-emerald-600"><Star className="w-4 h-4" /></Button>
                    )}
                    <Button size="sm" variant="ghost" data-testid={`delete-pm-${pm.id}`}
                      onClick={async () => { try { await api.delete(`/workspaces/${workspaceId}/payment-methods/${pm.id}`); toast.success(tr("billing:panel.moyenSupprime")); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } }}
                      className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-slate-400 mt-3">
          {tr("billing:v1.cardsNote")}
        </p>
      </div>

      {/* Résiliation (§43) */}
      {canManage && plan !== "free" && (
        <div className="glass rounded-2xl p-6" data-testid="cancel-subscription-section">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><Ban className="w-5 h-5 text-red-600" />{tr("billing:panel.resiliation")}</h3>
              <p className="text-sm text-slate-500 mt-1">{tr("billing:panel.laResiliationPrendEffet")}</p>
            </div>
            <Button data-testid="cancel-subscription-btn" variant="outline"
              className="border-red-200 bg-red-50 hover:bg-red-100 text-red-700"
              onClick={async () => { if (!window.confirm(tr("billing:panel.programmerLaResiliationA"))) return; try { await api.post(`/workspaces/${workspaceId}/billing/cancel-subscription`); toast.success(tr("billing:panel.resiliationPlanifiee")); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } }}>
              {tr("billing:panel.resilierLAbonnement")}
            </Button>
          </div>
        </div>
      )}

      {/* Documents financiers (§38) */}
      <div className="glass rounded-2xl p-6" data-testid="documents-section">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-cyan-600" />
          <h3 className="font-heading text-lg font-semibold">{tr("billing:panel.documentsFinanciers")}</h3>
        </div>
        <div className="flex flex-wrap gap-3 mb-4">
          <Select value={filters.type} onValueChange={(v) => setFilters((f) => ({ ...f, type: v }))}>
            <SelectTrigger data-testid="docs-filter-type" className="w-[160px] bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{tr("billing:panel.tousTypes")}</SelectItem>
              <SelectItem value="invoice">{tr("billing:panel.factures")}</SelectItem>
              <SelectItem value="credit_note">{tr("billing:panel.avoirs")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
            <SelectTrigger data-testid="docs-filter-status" className="w-[160px] bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{tr("billing:panel.tousStatuts")}</SelectItem>
              <SelectItem value="paid">{tr("billing:panel.payee")}</SelectItem>
              <SelectItem value="pending">{tr("billing:panel.enAttente")}</SelectItem>
              <SelectItem value="unpaid">{tr("billing:panel.impayee")}</SelectItem>
              <SelectItem value="overdue">{tr("billing:panel.enRetard")}</SelectItem>
              <SelectItem value="cancelled">{tr("billing:panel.annulee")}</SelectItem>
              <SelectItem value="credited">{tr("billing:panel.avoirEmis")}</SelectItem>
            </SelectContent>
          </Select>
          <Input data-testid="docs-filter-q" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder={tr("billing:panel.rechercherUnNumero")} className="w-[220px] bg-slate-50 border-slate-200" />
        </div>
        {docs.length === 0 ? (
          <p className="text-sm text-slate-500 py-8 text-center">{tr("billing:panel.aucunDocumentPourCes")}</p>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="documents-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{tr("billing:panel.type")}</TableHead><TableHead>{tr("billing:panel.numero")}</TableHead><TableHead>{tr("billing:panel.date")}</TableHead>
                  <TableHead>{tr("billing:panel.periode")}</TableHead><TableHead>{tr("billing:panel.montant")}</TableHead><TableHead>{tr("billing:panel.statut2")}</TableHead>
                  <TableHead className="text-right">{tr("billing:panel.pdf")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {docs.map((d) => (
                  <TableRow key={d.id} data-testid={`document-row-${d.id}`}>
                    <TableCell>{tr(`billing:docType.${d.doc_type}`, { defaultValue: d.doc_type_label })}</TableCell>
                    <TableCell className="font-medium">{d.number}</TableCell>
                    <TableCell className="text-slate-500">{d.date ? new Date(d.date).toLocaleDateString(intlLocaleFor(i18n.language)) : "—"}</TableCell>
                    <TableCell className="text-slate-500">{d.period || "—"}</TableCell>
                    <TableCell>{fmtAmountMinor(d.amount_minor || 0, d.currency)}</TableCell>
                    <TableCell><Badge variant="outline" className={DOC_STATUS_BADGE[d.status] || ""}>{tr(`billing:docStatus.${d.status}`, { defaultValue: d.status_label })}</Badge></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {canManage && d.can_settle && (
                        <Button size="sm" data-testid={`settle-doc-${d.id}`} disabled={!!redirecting}
                          onClick={() => goProvider(d.id, `/workspaces/${workspaceId}/billing/documents/${d.id}/settle`)}
                          className="mr-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
                          {redirecting === d.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}{tr("billing:v1.payNow")}
                        </Button>
                      )}
                      <Button size="sm" variant="outline" data-testid={`download-doc-${d.id}`} onClick={() => downloadDoc(d)}
                        className="border-slate-200 bg-white hover:bg-slate-100 text-slate-900">
                        <Download className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

    </div>
  );
};
